import { cn } from '@/lib/utils'
import { QUADRANTS, type Quadrant, type QuadrantKey } from './quadrants'

/** Static class names per quadrant: Tailwind reads source text, so these are never built from a template. */
const TONE: Record<QuadrantKey, { box: string; title: string; ring: string }> = {
  do_now: { box: 'bg-danger-soft', title: 'text-danger', ring: 'ring-danger' },
  schedule: { box: 'bg-info-soft', title: 'text-info', ring: 'ring-info' },
  delegate: { box: 'bg-warning-soft', title: 'text-warning', ring: 'ring-warning' },
  eliminate: { box: 'bg-neutral-soft', title: 'text-text-secondary', ring: 'ring-text-secondary' },
}

export const QUADRANT_CHIP: Record<QuadrantKey, string> = {
  do_now: 'bg-danger-soft text-danger',
  schedule: 'bg-info-soft text-info',
  delegate: 'bg-warning-soft text-warning',
  eliminate: 'bg-neutral-soft text-text-secondary',
}

/**
 * Two questions answered in one click: choosing a box answers both "is it urgent?" and "is it important?".
 * Clicking the chosen box again clears the answer, which leaves the item blank rather than guessed.
 */
export function MatrixPicker({
  value,
  onChange,
  compact = false,
  disabled = false,
}: {
  value: Quadrant | null
  onChange: (next: Quadrant | null) => void
  compact?: boolean
  disabled?: boolean
}) {
  const order = [QUADRANTS[1], QUADRANTS[0], QUADRANTS[3], QUADRANTS[2]] as Quadrant[]
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[auto_1fr_1fr] items-end gap-x-2 gap-y-1 text-tiny font-medium text-text-muted">
        <span />
        <span className="text-center">Not urgent</span>
        <span className="text-center">Urgent</span>
      </div>
      <div className="grid grid-cols-[auto_1fr_1fr] gap-2">
        {[0, 1].map((row) => (
          <div key={row} className="contents">
            <span className="flex items-center text-tiny font-medium text-text-muted [writing-mode:vertical-rl] rotate-180 justify-center">
              {row === 0 ? 'Important' : 'Not important'}
            </span>
            {order.slice(row * 2, row * 2 + 2).map((q) => {
              const tone = TONE[q.key]
              const selected = value?.key === q.key
              return (
                <button
                  key={q.key}
                  type="button"
                  disabled={disabled}
                  aria-pressed={selected}
                  onClick={() => onChange(selected ? null : q)}
                  className={cn(
                    'rounded-lg p-2.5 text-left transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
                    tone.box,
                    selected ? cn('ring-2', tone.ring) : 'hover:shadow-sm',
                  )}
                >
                  <span className={cn('block text-sm font-semibold', tone.title)}>{q.title}</span>
                  <span className="block text-tiny text-text-secondary">{q.short}</span>
                  {compact ? null : (
                    <>
                      <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-tiny text-foreground">
                        {q.examples.map((e) => (
                          <li key={e}>{e}</li>
                        ))}
                      </ul>
                      <span className="mt-1.5 block text-tiny font-medium text-foreground">{q.action}</span>
                    </>
                  )}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
